import * as R from 'ramda'

export const EVERYONE_GROUP = 1
export const HOMELESS_GROUP = 2
export const EXTERNAL_GROUP = 3
export const ORDINARY_GROUP = 0

export const EVERYONE_GROUP_NAME = 'Everyone'
export const HOMELESS_GROUP_NAME = 'Subscribers without a Group'

const groupToOrd = R.compose(
  R.cond([
    [R.equals(EVERYONE_GROUP), R.always(1000)],
    [R.equals(HOMELESS_GROUP), R.always(100)],
    [R.T, R.always(10)],
  ]),
  R.prop('type'),
)

export const sortGroupByType = R.sort((a, b) => groupToOrd(b) - groupToOrd(a))

export const isHomelessGroup = R.propEq('type', HOMELESS_GROUP)
export const isEveryoneGroup = R.propEq('type', EVERYONE_GROUP)
export const isOrdinaryGroup = R.propEq('type', ORDINARY_GROUP)
export const isEditable = (type) => type === ORDINARY_GROUP

export const isGeneralOrExternalGroup = (type) =>
  [ORDINARY_GROUP, EXTERNAL_GROUP].includes(type)

export const accessibleSubgroups = (subgroups) =>
  R.filter(
    R.isEmpty(subgroups)
      ? R.T
      : R.compose(R.includes(R.__, subgroups), R.prop('id')),
  )

export const subscriberCountByGroup = R.compose(
  R.sum,
  R.map(R.always(1)),
  R.reduce(
    (acc, tag) =>
      R.compose(
        R.uniqWith(R.equals),
        R.concat(acc),
        R.map(R.prop('id')),
        R.prop('subscribers'),
      )(tag),
    [],
  ),
  R.propOr([], 'tags'),
)

// displaySubgroupNames :: Group -> String
export const displaySubgroupNames = R.compose(
  R.sort((a, b) => a.localeCompare(b)),
  R.map((tag) => tag.name),
  R.filter(R.compose(isOrdinaryGroup, R.prop('group'))),
)

const parseGroup = (group) => {
  // boolean to indicate that this is not orinary group
  const isSpecialGroup = !isOrdinaryGroup(group)
  return {
    ...group,
    disabled: isSpecialGroup,
    isChildrenHidden: isSpecialGroup,
    children: group.tags,
  }
}

// formatter for select dropdown item component
export const getGroupOptions = (formatMessage, groups) =>
  R.compose(
    sortGroupByType,
    R.map(R.compose(parseGroup, formatGroup(formatMessage))),
  )(groups)

export const formatGroup = R.curry((formatMessage, group) =>
  R.pipe(
    formatHomelessGroup(formatMessage),
    formatEveryoneGroup(formatMessage),
  )(group),
)

export const formatHomelessGroup = R.curry((formatMessage, group) =>
  R.when(
    isHomelessGroup,
    R.compose(
      R.set(R.lensPath(['tags', 0, 'name']), homelessGroupName(formatMessage)),
      R.set(R.lensProp('name'), homelessGroupName(formatMessage)),
    ),
  )(group),
)

export const formatEveryoneGroup = R.curry((formatMessage, group) =>
  R.when(
    isEveryoneGroup,
    R.compose(R.set(R.lensProp('name'), everyoneGroupName(formatMessage))),
  )(group),
)

// formatter for recipient display component
export const getSubGroupOptions = (formatMessage, groups) =>
  R.compose(sortGroupByType, R.map(formatSubGroup(formatMessage)))(groups)

export const formatSubGroup = R.curry((formatMessage, group) =>
  R.pipe(
    formatHomelessSubgroup(formatMessage),
    formatEveryoneSubgroup(formatMessage),
  )(group),
)

export const formatHomelessSubgroup = R.curry((formatMessage, subgroup) =>
  R.when(
    R.propEq('name', HOMELESS_GROUP_NAME),
    R.set(R.lensProp('name'), homelessGroupName(formatMessage)),
  )(subgroup),
)

export const formatEveryoneSubgroup = R.curry((formatMessage, subgroup) =>
  R.when(
    R.propEq('name', EVERYONE_GROUP_NAME),
    R.set(R.lensProp('name'), everyoneGroupName(formatMessage)),
  )(subgroup),
)

export const homelessGroupName = (formatMessage) =>
  formatMessage({ id: 'group.subscribersNotInGroup.title' })

export const everyoneGroupName = (formatMessage) =>
  formatMessage({ id: 'group.everyone.title' })

export const subgroupDistributionTitle = (formatMessage) =>
  R.compose(
    R.join(', '),
    R.map(([name, subgroups]) =>
      R.cond([
        [
          R.equals(EVERYONE_GROUP_NAME),
          R.always(everyoneGroupName(formatMessage)),
        ],
        [
          R.equals(HOMELESS_GROUP_NAME),
          R.always(homelessGroupName(formatMessage)),
        ],
        [
          R.T,
          R.always(
            R.join('', [
              name,
              ' (',
              R.join(', ', R.map(R.prop('name'), subgroups)),
              ')',
            ]),
          ),
        ],
      ])(name),
    ),
    R.toPairs,
    R.groupBy(R.path(['group', 'name'])),
  )
